import { isWeekend, isWithinInterval, parse } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { WorkingHours } from '@/types/configuration';
import { CurrencyCodeType } from '@/types/currency';

/**
 * Checks if the current time is within Brazilian market hours.
 *
 * Market hours are provided by the API configuration and typically are
 * Monday to Friday, 9:00 AM - 3:40 PM BRT (Brazil Time).
 *
 * Note: The early close time (3:40 PM instead of typical 5:45 PM market close)
 * accounts for the 2-hour expiration window required for Pix QR codes used in
 * Cainvest-funded operations, ensuring operations complete before market close.
 *
 * @param marketHours - The market hours configuration from the API
 * @param date - Optional date to check. If not provided, uses current date/time.
 * @returns true if within Brazilian market hours, false otherwise
 */
export const isBrazilianMarketHours = (
  marketHours: WorkingHours,
  date: Date = new Date(),
): boolean => {
  // Convert the date to the market's timezone
  const zonedDate = toZonedTime(date, marketHours.timezone);

  // Check if it's a weekend
  if (isWeekend(zonedDate)) {
    return false;
  }

  // Parse market hours using the zoned date as reference
  const startTime = parse(marketHours.start, 'HH:mm', zonedDate);
  const endTime = parse(marketHours.end, 'HH:mm', zonedDate);

  // Check if current time is within the market hours interval
  return isWithinInterval(zonedDate, { start: startTime, end: endTime });
};

/**
 * Determines if a BRL (Brazilian Real) operation is outside Brazilian market hours.
 *
 * Returns true when both conditions are met:
 * 1. The currency is BRL (Brazilian Real)
 * 2. The current time is outside Brazilian market hours (typically Mon-Fri, 9:00 AM - 3:40 PM BRT)
 *
 * @param currency - The currency code to check for market hours restrictions
 * @param marketHours - The market hours configuration from the API
 * @param date - Optional date to check. If not provided, uses current date/time.
 * @returns true if the currency is BRL AND it's currently outside Brazilian market hours,
 *          false otherwise (non-BRL currencies or within market hours)
 *
 * @see {@link isBrazilianMarketHours} for the underlying market hours validation logic
 *
 * @remarks
 * This restriction exists due to Cainvest, the FX bank partner in Brazil, which limits
 * FX operations to market hours on business days. The 3:40 PM close time accounts for
 * the 2-hour Pix QR code expiration window required for deposit/transfer operations.
 */
export const isBRLOperationOutsideMarketHours = (
  currency: CurrencyCodeType,
  marketHours: WorkingHours,
  date?: Date,
): boolean => {
  return currency === CurrencyCodeType.BRL && !isBrazilianMarketHours(marketHours, date);
};
